import { useCallback } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactSortable } from "react-sortablejs";
import { getLayoutProductSlots } from "../../../features/layouts/layout.product-slots";
import { slotColor } from "../../../features/layouts/layout.slot-colors";
import { LayoutRef, LayoutItemRef } from "../../../features/layouts/layouts";
import { FixedPanel } from "../../../components/FixedPanel";
import { IconTypes } from "../../../components/icons.types";
import {
  DesignItemRef,
  getPositonAttr,
  getItemDescription,
} from "../../../features/design/design.types";
import {
  DesignerBoardApi,
  DesignerChange,
  canEditItem,
} from "../designer.board.api";
import { useEditors } from "../useEditors";
import { DesignSidebarRef } from "../catalogs/CatalogSidebar";
import { BoardTabIndex } from "../catalogs/designer-types";
import { computeSlotBinding } from "../layouts/layout.preview";
import { ProductSlotPicker } from "../layouts/ProductSlotPicker";

const thumbSize = 30;

type Props = {
  api: DesignerBoardApi;
  onChange: (change: DesignerChange) => void;
  pageItems: DesignItemRef[];
  readonly?: boolean;
  sidebarRef?: DesignSidebarRef;
  showPageSettings?: boolean;
  /** Layout designer only — enables per-slot colour, product names, cross-highlight
   *  and the "same product as" picker. Other designers pass none of these. */
  layout?: LayoutRef;
  sampleOffset?: number;
  slotOverrides?: Map<number, string>;
  hoveredSlot?: number | null;
  onHoverSlot?: (s: number | null) => void;
};

export const DesignerLayers = ({
  api,
  pageItems,
  onChange,
  readonly,
  sidebarRef,
  showPageSettings,
  layout,
  sampleOffset,
  slotOverrides,
  hoveredSlot,
  onHoverSlot,
}: Props) => {
  const { openProductListExtEditor, openItemProductEdit } = useEditors();

  const positionAttr = getPositonAttr(api.meta);

  const page = api?.builder.getActivePage();
  // CM2-2GS: pageItems can be undefined on an intermediate render (page not yet
  // populated in the builder); .filter then throws and blanks the designer. Coalesce.
  const safePageItems = pageItems ?? [];
  const items = safePageItems
    .filter((i) => i.type !== "page")
    .sort((a, b) => -(a.di[positionAttr] ?? 0) + (b.di[positionAttr] ?? 0));

  // Layout designer only: how products are assigned across this layout's elements.
  const isLayout = api.meta === "layout";
  const slots = isLayout ? getLayoutProductSlots(safePageItems) : null;
  const slotBinding =
    isLayout && slots
      ? computeSlotBinding(api.builder, slots, sampleOffset ?? 0, slotOverrides)
      : null;
  const selectedSlotItem =
    isLayout && slots && api.selectedItem && slots.slotById.has(api.selectedItem.id)
      ? (api.selectedItem as LayoutItemRef)
      : null;

  const handlePageSettingsClick = () => {
    sidebarRef?.setTab(BoardTabIndex.Settings, "page");
  };

  const renderActions = useCallback(
    (item: DesignItemRef) => {
      return (
        <>
          {canEditItem(item) && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => api.editItem(item.id)}
            >
              <IconTypes.Edit fontSize="small" />
            </IconButton>
          )}
          {item.info.product && item.meta === "catalog" && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => openItemProductEdit(item, onChange)}
            >
              <IconTypes.Product fontSize="small" />
            </IconButton>
          )}

          {item.info.extension && (
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                openProductListExtEditor(item, api.builder, onChange)
              }
            >
              <IconTypes.Settings fontSize="small" />
            </IconButton>
          )}
        </>
      );
    },
    [api, onChange, openItemProductEdit, openProductListExtEditor]
  );

  const renderProductSlot = (item: DesignItemRef) => {
    if (!isLayout || !slots) return null;
    const slot = slots.slotById.get(item.id);
    if (!slot) return null;
    const key = slots.keyById.get(item.id);
    const isCard = !!item.info?.template;
    const productName = slotBinding?.productBySlot.get(slot)?.product.name;
    const color = slotColor(slot);
    const hovered = hoveredSlot === slot;

    // Compact slot badge: P{slot} + key if present. The product name rides a
    // second line in the row (keeps the row from being cramped); full detail in tip.
    const label = key ? `P${slot} · ${key}` : `P${slot}`;
    const tip = [
      `Product ${slot}${productName ? ` — ${productName}` : ""}`,
      key
        ? `Shared key "${key}"`
        : isCard
        ? undefined
        : "Uses its own product — pick a product to share one with a card.",
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <Tooltip title={tip}>
        <Chip
          size="small"
          icon={
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: color,
                boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
              }}
            />
          }
          label={label}
          sx={{
            height: 20,
            flexShrink: 0,
            maxWidth: 120,
            fontWeight: 600,
            color: (t) => t.palette.getContrastText(alpha(color, 1)),
            bgcolor: alpha(color, hovered ? 0.5 : 0.22),
            border: `1px solid ${alpha(color, hovered ? 0.9 : 0.5)}`,
            "& .MuiChip-icon": { ml: "6px" },
          }}
        />
      </Tooltip>
    );
  };

  if (!page || !items) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .layer-element": {
          cursor: "pointer",
          p: 1,
          mb: 1,
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 50,

          button: {
            display: "none",
          },

          "&.selected , &:hover": {
            backgroundColor: "action.selected",
            button: {
              display: "inline-flex",
            },
          },

          "& .actions": {
            display: "flex",
            justifyContent: "flex-end",
            button: {
              color: "text.secondary",
            },
          },
          "& .layer-drag": {
            width: 20,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          "& .layer-thumb": {
            minWidth: thumbSize + 20,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "& img": {
              maxWidth: thumbSize + 10,
              maxHeight: thumbSize,
            },
            "& .bg": {
              backgroundColor: "text.secondary",
            },
            "& svg": {
              color: "text.secondary",
            },
          },
          "& .title": {
            flexGrow: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        },
      }}
    >
      {!items.length && (
        <Box sx={{ p: 2, mt: 2, textAlign: "center" }}>
          <Typography variant="subtitle1">No elements</Typography>
        </Box>
      )}
      {items.length > 0 && !readonly && (
        <Box sx={{ mb: 1, ml: 1, px: 2 }}>
          <Typography variant="caption">
            Reorder, edit or delete elements
          </Typography>
        </Box>
      )}

      {isLayout && slots && items.length > 0 && (
        <Box
          sx={{
            mx: 1,
            mb: 1,
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: slots.warn ? "warning.main" : "divider",
            backgroundColor: "action.hover",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconTypes.Product
              fontSize="small"
              color={slots.warn ? "warning" : "action"}
            />
            <Typography variant="body2">
              <b>{slots.total}</b> product{slots.total === 1 ? "" : "s"} per page
              {slots.hasList ? " + list" : ""}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {slots.cardCount} card{slots.cardCount === 1 ? "" : "s"}
            {slots.total > slots.cardCount
              ? ` + ${slots.total - slots.cardCount} other`
              : ""}
          </Typography>
          {slots.warn && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: "block", mt: 0.5 }}
            >
              An element uses its own product. Give it the same Product Key as a
              card to share one.
            </Typography>
          )}
        </Box>
      )}

      <FixedPanel overflow="auto">
        <Box sx={{ px: 1 }}>
          <ReactSortable
            list={items}
            disabled={readonly}
            setList={(newList) => {
              if (!readonly) {
                refreshPositions(newList, positionAttr);
                onChange({ type: "refresh", action: "reorder elements" });
              }
            }}
          >
            {items.map((item) => {
              const rowSlot =
                isLayout && slots ? slots.slotById.get(item.id) : undefined;
              const rowHovered = !!rowSlot && hoveredSlot === rowSlot;
              const rowProductName = rowSlot
                ? slotBinding?.productBySlot.get(rowSlot)?.product.name
                : undefined;
              return (
              <Paper
                key={item.id}
                className={`layer-element ${
                  api.selectedItem?.id === item.id ? "selected" : ""
                }`}
                onMouseEnter={
                  isLayout ? () => onHoverSlot?.(rowSlot ?? null) : undefined
                }
                onMouseLeave={isLayout ? () => onHoverSlot?.(null) : undefined}
                sx={
                  rowHovered
                    ? {
                        boxShadow: `inset 3px 0 0 ${slotColor(rowSlot)}`,
                        backgroundColor: "action.hover",
                      }
                    : undefined
                }
                onClick={
                  readonly
                    ? null
                    : () => api.boardRef.selectBoardItem(item, "click")
                }
              >
                <div className="layer-drag">
                  <IconTypes.Drag fontSize="small" />
                </div>

                <div className="layer-thumb">{renderElement(item)}</div>

                {renderProductSlot(item)}

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: getItemDescription(item, false),
                    }}
                  />
                  {rowProductName && (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        display: "block",
                        color: slotColor(rowSlot!),
                        fontWeight: 600,
                      }}
                    >
                      {rowProductName}
                    </Typography>
                  )}
                </Box>
                {!readonly && (
                  <div className="actions">
                    {renderActions(item)}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onChange({ type: "remove", action: "remove", item });
                      }}
                    >
                      <IconTypes.DeleteAction />
                    </IconButton>
                  </div>
                )}
              </Paper>
              );
            })}
          </ReactSortable>
        </Box>

        {selectedSlotItem && layout && slots && slotBinding && !readonly && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              mt: "auto",
              backgroundColor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Selected element — which product does it show?
            </Typography>
            <ProductSlotPicker
              item={selectedSlotItem}
              layout={layout}
              slots={slots}
              binding={slotBinding}
              onChange={onChange}
            />
          </Box>
        )}

        {showPageSettings && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              mt: "auto",
              backgroundColor: "background.paper",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Looking for page background or other page settings?
            </Typography>
            <Button
              size="small"
              onClick={handlePageSettingsClick}
              startIcon={<IconTypes.Settings />}
            >
              Page Settings
            </Button>
          </Box>
        )}
      </FixedPanel>
    </Box>
  );
};

const renderElement = (item: DesignItemRef) => {
  if (item.info.extension) return <IconTypes.ProductList />;
  if (item.info.template) return <IconTypes.ProductCard />;
  switch (item.type) {
    case "image": {
      return item.info.src && !item.info.imageField ? (
        <img src={item.info.src} alt={item.info.path} title={item.info.path} />
      ) : (
        <IconTypes.Image />
      );
    }
    case "bg": {
      return (
        <Box
          className="bg"
          style={{
            ...item.style,
            width: thumbSize,
            height: thumbSize,
            borderWidth: 2,
          }}
        ></Box>
      );
    }
    case "text": {
      return <IconTypes.TextPlaceholder />;
    }
    default:
      return "";
  }
};

const refreshPositions = (newList: DesignItemRef[], positionAttr: string) => {
  newList.forEach((item, i) => {
    const pos = newList.length - i - 1;
    item.di[positionAttr] = pos;
    item.style = { ...item.style, zIndex: pos };
  });
};
